import React, { useState } from 'react';
import {
  Plus,
  Search,
  MessageSquare,
  Code2,
  Bug,
  Server,
  Layers,
  Palette,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  MoreHorizontal,
  Trash2,
  Archive
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

/**
 * ConversationSidebar - Left panel for chat history and task list
 */
const ConversationSidebar = ({
  conversations = [],
  selectedId,
  onSelect,
  onNewChat,
  onDelete,
  onArchive,
  filter = 'all',
  onFilterChange,
  loading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const filters = [
    { value: 'all', label: 'All', icon: MessageSquare, count: conversations.length },
    { value: 'ui', label: 'UI Fix', icon: Palette, count: conversations.filter(c => c.type === 'ui').length },
    { value: 'bug', label: 'Bug', icon: Bug, count: conversations.filter(c => c.type === 'bug').length },
    { value: 'backend', label: 'Backend', icon: Server, count: conversations.filter(c => c.type === 'backend').length },
    { value: 'feature', label: 'Feature', icon: Layers, count: conversations.filter(c => c.type === 'feature').length },
    { value: 'refactor', label: 'Refactor', icon: RefreshCw, count: conversations.filter(c => c.type === 'refactor').length }
  ];

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      inspecting: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      patch_ready: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      executed: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
      rolled_back: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
    };
    return colors[status] || colors.planning;
  };

  const getStatusIcon = (status) => {
    const icons = {
      planning: <Clock className="w-3 h-3" />,
      inspecting: <Search className="w-3 h-3" />,
      patch_ready: <Code2 className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      executed: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
      rolled_back: <RefreshCw className="w-3 h-3" />
    };
    return icons[status] || icons.planning;
  };

  const getTypeIcon = (type) => {
    const icons = {
      ui: <Palette className="w-3.5 h-3.5 text-purple-400" />,
      bug: <Bug className="w-3.5 h-3.5 text-red-400" />,
      backend: <Server className="w-3.5 h-3.5 text-amber-400" />,
      feature: <Layers className="w-3.5 h-3.5 text-blue-400" />,
      refactor: <RefreshCw className="w-3.5 h-3.5 text-cyan-400" />
    };
    return icons[type] || <Code2 className="w-3.5 h-3.5 text-slate-400" />;
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.preview?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || conv.type === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="h-full flex flex-col bg-[rgba(10,38,28,0.4)] border-r border-[rgba(142,218,182,0.1)]">
      {/* Header */}
      <div className="p-4 border-b border-slate-700/50">
        <Button
          onClick={onNewChat}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          data-testid="new-chat-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Search */}
      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="pl-10 bg-slate-800/50 border-slate-700 text-white text-sm rounded-xl"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="px-3 pb-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-300 mb-2"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? '' : '-rotate-90'}`} />
          Filters
        </button>
        {showFilters && (
          <div className="flex flex-wrap gap-1">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => onFilterChange?.(f.value)}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
                  filter === f.value
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-slate-800/30 text-slate-400 border border-transparent hover:border-slate-700'
                }`}
              >
                <f.icon className="w-3 h-3" />
                {f.label}
                {f.count > 0 && (
                  <span className="ml-1 opacity-60">{f.count}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-emerald-500" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-600" />
            <p className="text-sm text-slate-500">No conversations yet</p>
            <p className="text-xs text-slate-600 mt-1">Start a new task to begin</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => onSelect?.(conv.id)}
                className={`group p-3 rounded-xl cursor-pointer transition-all ${
                  selectedId === conv.id
                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                    : 'hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(conv.type)}
                    <span className="text-sm font-medium text-white line-clamp-1">
                      {conv.title || 'Untitled Task'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); onArchive?.(conv.id); }}
                      className="p-1 hover:bg-slate-700 rounded"
                    >
                      <Archive className="w-3 h-3 text-slate-400" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete?.(conv.id); }}
                      className="p-1 hover:bg-slate-700 rounded"
                    >
                      <Trash2 className="w-3 h-3 text-slate-400" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2 mb-2">
                  {conv.preview || 'No messages yet'}
                </p>
                <div className="flex items-center justify-between">
                  <Badge className={`${getStatusColor(conv.status)} border text-[10px] flex items-center gap-1`}>
                    {getStatusIcon(conv.status)}
                    {conv.status?.replace('_', ' ')}
                  </Badge>
                  <span className="text-[10px] text-slate-600">
                    {conv.updatedAt ? new Date(conv.updatedAt).toLocaleDateString() : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationSidebar;
